/**
 * EditPaymentAdjustmentDialog - Handles payment adjustments when editing a sale
 * 
 * When items are removed (refund scenario):
 *   - Shows original paid, new total, and refund amount
 *   - Payment modes: CASH, CARD, or STORE CREDIT/ADVANCE
 * 
 * When items are added (collect balance scenario):
 *   - Shows original paid, new total, and balance to collect
 *   - Payment modes: CASH, UPI, or CREDIT
 */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Banknote, 
  CreditCard, 
  Smartphone, 
  Wallet, 
  ArrowDownCircle, 
  ArrowUpCircle,
  AlertCircle
} from "lucide-react";

function r2(v) {
  return Math.round(Number(v || 0) * 100) / 100;
}

export default function EditPaymentAdjustmentDialog({
  open,
  onOpenChange,
  originalTotal,
  originalPaid,
  newTotal,
  originalPayments = [],
  customer = null,
  onConfirm,
  saving = false,
}) {
  const [selectedMode, setSelectedMode] = useState(null);
  const [amount, setAmount] = useState(0);
  // Generate stable adjustment_id ONCE when dialog opens for a new adjustment event
  const [adjustmentId, setAdjustmentId] = useState(null);
  
  const difference = r2(newTotal - originalPaid);
  const isRefund = difference < 0;
  const adjustmentAmount = Math.abs(difference);
  
  // For refunds: CASH, CARD, STORE_CREDIT
  // For collection: CASH, UPI, CREDIT
  const refundModes = [
    { value: "cash", label: "Cash", icon: Banknote, color: "text-green-600 bg-green-50 border-green-200 hover:bg-green-100" },
    { value: "card", label: "Card", icon: CreditCard, color: "text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100" },
    { value: "store_credit", label: "Store Credit", icon: Wallet, color: "text-purple-600 bg-purple-50 border-purple-200 hover:bg-purple-100" },
  ];
  
  const collectModes = [
    { value: "cash", label: "Cash", icon: Banknote, color: "text-green-600 bg-green-50 border-green-200 hover:bg-green-100" },
    { value: "upi", label: "UPI", icon: Smartphone, color: "text-orange-600 bg-orange-50 border-orange-200 hover:bg-orange-100" },
    { value: "credit", label: "Credit", icon: CreditCard, color: "text-red-600 bg-red-50 border-red-200 hover:bg-red-100" },
  ];
  
  const paymentModes = isRefund ? refundModes : collectModes;
  
  // Reset state when dialog opens - generate NEW adjustment_id for each new user action
  useEffect(() => {
    if (open) {
      setSelectedMode(null);
      setAmount(adjustmentAmount);
      // Generate new idempotency key for this payment adjustment event
      // This same ID will be reused if the request needs to be retried
      setAdjustmentId(crypto.randomUUID());
    }
  }, [open, adjustmentAmount]);
  
  // Require customer for store_credit refund or credit collection
  const needsCustomer = (selectedMode === "store_credit" || selectedMode === "credit") && !customer;
  
  const handleConfirm = () => {
    if (!selectedMode) return;
    if (needsCustomer) return;
    
    // Pass the stable adjustment_id - will be reused on retry
    onConfirm({
      adjustment_id: adjustmentId,
      mode: selectedMode,
      amount: r2(amount),
      isRefund,
      originalPayments,
    });
  };
  
  // Don't show if no adjustment needed
  if (Math.abs(difference) < 0.01) {
    return null;
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-payment-adjustment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isRefund ? (
              <>
                <ArrowDownCircle className="w-5 h-5 text-green-600" />
                Refund Due to Customer
              </>
            ) : (
              <>
                <ArrowUpCircle className="w-5 h-5 text-red-600" />
                Balance to Collect
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isRefund 
              ? "Items were removed. Select how to refund the customer."
              : "Items were added. Select how to collect the balance."}
          </DialogDescription>
        </DialogHeader>
        
        {/* Summary Section */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Original Paid:</span>
            <span className="font-mono font-medium">₹{originalPaid.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">New Total:</span>
            <span className="font-mono font-medium">₹{newTotal.toFixed(2)}</span>
          </div>
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between text-base font-semibold">
              <span className={isRefund ? "text-green-700" : "text-red-700"}>
                {isRefund ? "Refund Amount:" : "Balance Due:"}
              </span>
              <span className={`font-mono ${isRefund ? "text-green-700" : "text-red-700"}`}>
                ₹{adjustmentAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Payment Mode Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            {isRefund ? "Refund Via:" : "Collect Via:"}
          </Label>
          <div className="grid grid-cols-3 gap-2">
            {paymentModes.map((mode) => {
              const Icon = mode.icon;
              const isSelected = selectedMode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setSelectedMode(mode.value)}
                  className={`
                    flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg border-2 transition-all
                    ${isSelected 
                      ? `${mode.color} border-current ring-2 ring-offset-1 ring-current/30` 
                      : "border-gray-200 hover:border-gray-300 bg-white"
                    }
                  `}
                  data-testid={`adjustment-mode-${mode.value}`}
                >
                  <Icon className={`w-5 h-5 ${isSelected ? "" : "text-gray-500"}`} />
                  <span className={`text-xs font-medium ${isSelected ? "" : "text-gray-600"}`}>
                    {mode.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Warning for store_credit/credit without customer */}
        {needsCustomer && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {isRefund 
                ? "Store Credit requires a customer to be selected on this sale."
                : "Credit requires a customer to be selected on this sale."}
            </span>
          </div>
        )}
        
        {/* Amount Input (pre-filled, but editable for partial adjustments) */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Amount</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
            <Input
              type="number"
              step="0.01"
              min={0}
              max={adjustmentAmount}
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value || "0"))}
              className="pl-7 font-mono text-right"
              data-testid="adjustment-amount-input"
            />
          </div>
          {amount !== adjustmentAmount && (
            <p className="text-xs text-muted-foreground">
              Remaining ₹{r2(adjustmentAmount - amount).toFixed(2)} will be {isRefund ? "kept as store credit" : "added to credit"}.
            </p>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedMode || needsCustomer || saving || amount <= 0}
            className={isRefund ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
            data-testid="confirm-adjustment-btn"
          >
            {saving ? "Processing..." : isRefund ? "Process Refund" : "Collect Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
